/**
 * CRAZXY Wishlist
 * Stores { id, handle, title, vendor, price, image, url, variantId } per product.
 * Fires 'czx:wishlist:updated' on document after every change.
 */
(function () {
  const STORAGE_KEY = 'czx_wishlist';
  const EVENT = 'czx:wishlist:updated';

  /* ── Storage helpers ── */
  function load() {
    try { return JSON.parse(localStorage.getItem(STORAGE_KEY)) || []; }
    catch { return []; }
  }
  function save(items) {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(items));
    document.dispatchEvent(new CustomEvent(EVENT, { detail: { items } }));
  }

  /* ── Public API ── */
  window.CzxWishlist = {
    getAll()        { return load(); },
    has(id)         { return load().some(p => String(p.id) === String(id)); },
    add(product)    {
      const items = load();
      if (!items.some(p => String(p.id) === String(product.id))) {
        items.push(product);
        save(items);
      }
    },
    remove(id)      { save(load().filter(p => String(p.id) !== String(id))); },
    toggle(product) { this.has(product.id) ? this.remove(product.id) : this.add(product); },
    count()         { return load().length; },
    openModal(p)    { czxWlOpenModal(p); },
  };

  /* ── Button sync: read localStorage ONCE, batch-update all buttons via rAF ── */
  let syncScheduled = false;
  function syncButtons() {
    if (syncScheduled) return;
    syncScheduled = true;
    requestAnimationFrame(function () {
      syncScheduled = false;
      const items = load();
      const ids = new Set(items.map(p => String(p.id)));
      document.querySelectorAll('.czx-wishlist-btn[data-product-id]').forEach(btn => {
        const active = ids.has(String(btn.dataset.productId));
        btn.classList.toggle('czx-wishlist-btn--active', active);
        btn.setAttribute('aria-pressed', active ? 'true' : 'false');
        /* Heart visibility is now fully controlled by CSS class (czx-wishlist-btn--active).
           Inline style overrides removed — they caused state desync on re-render. */
        const label = btn.querySelector('.czx-wishlist-btn__label');
        if (label) label.textContent = active ? 'Wishlisted ♥' : 'Add to Wishlist ♡';
      });
      const c = ids.size;
      document.querySelectorAll('.czx-wishlist-count').forEach(el => {
        el.textContent = c > 0 ? c : '';
      });
    });
  }

  document.addEventListener(EVENT, syncButtons);
  document.addEventListener('DOMContentLoaded', syncButtons);

  /* ── Wishlist button click handler (delegated — handles all .czx-wishlist-btn buttons) ── */
  document.addEventListener('click', function (e) {
    const btn = e.target.closest('.czx-wishlist-btn[data-product-id]');
    if (!btn) return;
    e.stopPropagation();
    if (!window.CzxWishlist) return;
    window.CzxWishlist.toggle({
      id:           btn.dataset.productId,
      handle:       btn.dataset.productHandle,
      title:        btn.dataset.productTitle,
      vendor:       btn.dataset.productVendor,
      price:        btn.dataset.productPrice,
      image:        btn.dataset.productImage,
      url:          btn.dataset.productUrl,
      variantId:    btn.dataset.variantId,
      insiderTags:  btn.dataset.insiderTags || '',
    });
    btn.animate(
      [{ transform: 'scale(0.88)' }, { transform: 'scale(1.08)' }, { transform: 'scale(1)' }],
      { duration: 300, easing: 'ease-out' }
    );
  });

  /* ── Wishlist page rendering ── */

  /* Load the Liquid-rendered insider tag map (handle → comma-separated tag handles) */
  var CZX_TAG_MAP = {};
  var _tagMapEl = document.getElementById('czx-insider-tag-map');
  if (_tagMapEl) {
    try { CZX_TAG_MAP = JSON.parse(_tagMapEl.textContent) || {}; } catch(e) {}
  }
  var CZX_MSG_MAP = {};
  var _msgMapEl = document.getElementById('czx-msg-meta-map');
  if (_msgMapEl) {
    try { CZX_MSG_MAP = JSON.parse(_msgMapEl.textContent) || {}; } catch(e) {}
  }
  var CZX_PHOTO_MAP = {};
  var _photoMapEl = document.getElementById('czx-photo-meta-map');
  if (_photoMapEl) {
    try { CZX_PHOTO_MAP = JSON.parse(_photoMapEl.textContent) || {}; } catch(e) {}
  }

  var TAG_DISPLAY = {
    'most-viral':   { label: 'VIRAL',      color: '#FF006E' },
    'new-arrivals': { label: 'NEW',        color: '#FF9F1C' },
    'ultra-rare':   { label: 'ULTRA RARE', color: '#7B2FBE' },
    'pre-order':    { label: 'PRE ORDER',  color: '#4361EE' },
    'seasonal':     { label: 'SEASONAL',   color: '#06D6A0' },
    'laaf':         { label: 'LAAF',       color: '#00B4D8' },
  };

  function renderWishlistPage() {
    const grid = document.getElementById('czx-wishlist-grid');
    const empty = document.getElementById('czx-wishlist-empty');
    if (!grid) return;

    const items = window.CzxWishlist.getAll();
    grid.innerHTML = '';

    if (items.length === 0) {
      grid.style.display = 'none';
      if (empty) empty.style.display = '';
      return;
    }

    grid.style.display = '';
    if (empty) empty.style.display = 'none';

    items.forEach(function (p) {
      const fmt = (cents) => (parseInt(cents, 10) / 100).toLocaleString('en-US', { style: 'currency', currency: 'USD' });
      const price = p.price ? fmt(p.price) : '';

      /* Use the same czx-card structure as the product card snippet */
      const card = document.createElement('div');
      card.className = 'czx-card';
      card.dataset.url = p.url || '';
      card.style.cssText = 'display:flex;flex-direction:column;';
      card.innerHTML = `
        <a href="${p.url}" class="czx-card__img-wrap" aria-label="${p.title}">
          ${p.image
            ? `<img class="czx-card__img" src="${p.image}" alt="${p.title}" loading="lazy">`
            : `<div style="width:100%;height:100%;display:flex;align-items:center;justify-content:center;font-size:56px;background:#f7f7f7;">🎉</div>`}
          <div class="czx-card__overlay" style="pointer-events:none;">
            <span class="czx-card__overlay-pill" style="pointer-events:auto;">View Details →</span>
          </div>
        </a>

        <div class="czx-card__body">
          <div class="czx-card__tag-row"></div>
          <p class="czx-card__title">${p.title}</p>
          <p class="czx-card__vendor">${p.vendor || ''}</p>
          <div class="czx-card__price-row">
            <span class="czx-card__price">${price}</span>
          </div>
          <div class="czx-wl-card__actions">
            <button class="czx-wl-card__atc" type="button">
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round"><circle cx="9" cy="21" r="1"/><circle cx="20" cy="21" r="1"/><path d="M1 1h4l2.68 13.39a2 2 0 0 0 2 1.61h9.72a2 2 0 0 0 2-1.61L23 6H6"/></svg>
              Add to Cart
            </button>
            <button class="czx-wl-card__remove" type="button" data-product-id="${p.id}" aria-label="Remove from wishlist">
              ✕ Remove
            </button>
          </div>
        </div>
      `;

      /* Render insider tags from the Liquid-generated tag map */
      var _tagHandles = CZX_TAG_MAP[p.handle] || p.insiderTags || '';
      if (_tagHandles) {
        var _tagRow = card.querySelector('.czx-card__tag-row');
        _tagHandles.split(',').forEach(function (handle) {
          var def = TAG_DISPLAY[handle.trim()];
          if (def) {
            var span = document.createElement('span');
            span.className = 'czx-card__tag';
            span.style.background = def.color;
            span.textContent = def.label;
            _tagRow.appendChild(span);
          }
        });
      }

      /* Add to cart — open dedicated modal */
      card.querySelector('.czx-wl-card__atc').addEventListener('click', function () {
        czxWlOpenModal(p);
      });

      /* Remove */
      card.querySelector('.czx-wl-card__remove').addEventListener('click', function () {
        window.CzxWishlist.remove(this.dataset.productId);
        renderWishlistPage();
      });

      grid.appendChild(card);
    });
  }

  document.addEventListener('DOMContentLoaded', renderWishlistPage);
  document.addEventListener(EVENT, renderWishlistPage);

  /* ── Add to Cart modal ── */
  async function czxWlOpenModal(p) {
    // Fetch live product data
    let product;
    try {
      const r = await fetch('/products/' + p.handle + '.js');
      product = await r.json();
    } catch {
      alert('Could not load product. Please try again.');
      return;
    }

    /* Fetch this product's custom field metafields live (reliable — not limited
       to collections['all']). Falls back to the pre-built maps if the fetch fails. */
    let liveMeta = null;
    try {
      const mr = await fetch('/products/' + p.handle + '?view=czx-meta');
      const txt = await mr.text();
      liveMeta = JSON.parse(txt.trim());
    } catch (e) {
      liveMeta = null;
    }

    document.getElementById('czx-wl-modal')?.remove();

    // State
    let qty = 1;
    const selected = {};  // optIdx -> value

    // Pre-select the saved variant's options
    if (p.variantId) {
      const savedVariant = product.variants.find(v => String(v.id) === String(p.variantId));
      if (savedVariant) {
        savedVariant.options.forEach((val, i) => { selected[i] = val; });
      }
    }

    /* ── Modal backdrop ── */
    const modal = document.createElement('div');
    modal.id = 'czx-wl-modal';
    modal.style.cssText = [
      'position:fixed;inset:0;z-index:9999',
      'display:flex;align-items:flex-end;justify-content:center',
      'background:rgba(0,0,0,0.55)',
      'padding:0',
      'font-family:Boogaloo,cursive',
    ].join(';');

    /* ── Sheet ── */
    const sheet = document.createElement('div');
    sheet.style.cssText = [
      'background:#fff',
      'border-radius:20px 20px 0 0',
      'width:100%;max-width:520px',
      'max-height:80svh',
      'overflow-y:auto',
      'padding:0 0 env(safe-area-inset-bottom,0)',
      'box-shadow:0 -8px 40px rgba(0,0,0,0.18)',
      'display:flex;flex-direction:column',
      'animation:czxSheetUp 0.28s cubic-bezier(0.32,0.72,0,1)',
    ].join(';');

    /* ── Drag handle ── */
    const handle = document.createElement('div');
    handle.style.cssText = 'width:40px;height:4px;background:rgba(0,0,0,0.15);border-radius:2px;margin:12px auto 8px;flex-shrink:0;';
    sheet.appendChild(handle);

    /* ── Body ── */
    const body = document.createElement('div');
    body.style.cssText = 'padding:0 20px 24px;display:flex;flex-direction:column;gap:14px;';

    /* ── Top row: small image + title/vendor/price + close ── */
    const topRow = document.createElement('div');
    topRow.style.cssText = 'display:flex;align-items:flex-start;gap:14px;';

    /* Small product image */
    const imgWrap = document.createElement('div');
    imgWrap.style.cssText = 'width:80px;height:80px;flex-shrink:0;border-radius:10px;background:#f5f5f5;overflow:hidden;border:1.5px solid rgba(0,0,0,0.08);';
    const imgEl = document.createElement('img');
    imgEl.src = p.image || '';
    imgEl.alt = p.title;
    imgEl.style.cssText = 'width:100%;height:100%;object-fit:cover;display:block;';
    imgWrap.appendChild(imgEl);
    topRow.appendChild(imgWrap);

    /* Title + vendor + price */
    const titleWrap = document.createElement('div');
    titleWrap.style.cssText = 'flex:1;min-width:0;display:flex;flex-direction:column;gap:2px;';
    const vendorEl = document.createElement('p');
    vendorEl.style.cssText = 'font-size:0.7rem;color:rgba(13,13,13,0.45);text-transform:uppercase;letter-spacing:0.06em;margin:0;';
    vendorEl.textContent = p.vendor || '';
    const titleEl = document.createElement('p');
    titleEl.style.cssText = 'font-size:1rem;font-weight:700;color:#0D0D0D;margin:0;line-height:1.25;';
    titleEl.textContent = p.title;
    const priceEl = document.createElement('p');
    priceEl.style.cssText = 'font-size:1.05rem;font-weight:700;color:#0D0D0D;margin:4px 0 0;font-family:Boogaloo,cursive;';
    function updatePrice() {
      const v = getSelectedVariant();
      if (v) {
        const fmt = (cents) => (cents / 100).toLocaleString('en-US', { style: 'currency', currency: 'USD' });
        priceEl.textContent = fmt(v.price);
      }
    }
    titleWrap.appendChild(vendorEl);
    titleWrap.appendChild(titleEl);
    titleWrap.appendChild(priceEl);
    topRow.appendChild(titleWrap);

    /* Close button */
    const closeBtn = document.createElement('button');
    closeBtn.innerHTML = '<svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round"><line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/></svg>';
    closeBtn.style.cssText = 'background:rgba(0,0,0,0.06);border:none;border-radius:50%;width:32px;height:32px;flex-shrink:0;display:flex;align-items:center;justify-content:center;cursor:pointer;color:#0D0D0D;margin-top:2px;';
    closeBtn.addEventListener('click', () => modal.remove());
    topRow.appendChild(closeBtn);

    body.appendChild(topRow);

    /* Divider */
    const divider = document.createElement('div');
    divider.style.cssText = 'height:1px;background:rgba(0,0,0,0.08);margin:0 -20px;';
    body.appendChild(divider);

    /* ── Variant options ── */
    const optionsWrap = document.createElement('div');
    optionsWrap.style.cssText = 'display:flex;flex-direction:column;gap:12px;';

    function getSelectedVariant() {
      return product.variants.find(v =>
        v.options.every((opt, i) => !product.options[i] || selected[i] === opt)
      ) || null;
    }

    function updateVariantImage() {
      const v = getSelectedVariant();
      if (v && v.featured_image && v.featured_image.src) {
        imgEl.src = v.featured_image.src;
      }
    }

    const isDefaultVariantOnly = product.variants.length === 1 && product.variants[0].title === 'Default Title';

    if (!isDefaultVariantOnly) product.options.forEach((optionRaw, optIdx) => {
      const optionName = typeof optionRaw === 'object' ? (optionRaw.name || String(optionRaw)) : String(optionRaw);
      const values = [...new Set(product.variants.map(v => v.options[optIdx]))];

      const group = document.createElement('div');

      const lbl = document.createElement('p');
      lbl.style.cssText = 'font-size:0.75rem;letter-spacing:0.08em;text-transform:uppercase;color:rgba(13,13,13,0.5);margin:0 0 8px;';
      lbl.textContent = optionName;
      group.appendChild(lbl);

      const btnRow = document.createElement('div');
      btnRow.style.cssText = 'display:flex;flex-wrap:wrap;gap:8px;';

      values.forEach(val => {
        const isAvailable = product.variants.some(v => v.options[optIdx] === val && v.available);
        const vBtn = document.createElement('button');
        vBtn.dataset.optIdx = String(optIdx);
        vBtn.dataset.val = val;
        vBtn.style.cssText = [
          'padding:7px 14px',
          'border:2px solid ' + (selected[optIdx] === val ? '#0D0D0D' : 'rgba(0,0,0,0.15)'),
          'border-radius:8px',
          'background:' + (selected[optIdx] === val ? '#0D0D0D' : '#fff'),
          'color:' + (selected[optIdx] === val ? '#fff' : (isAvailable ? '#0D0D0D' : 'rgba(13,13,13,0.3)')),
          'font-family:Boogaloo,cursive',
          'font-size:0.9rem',
          'cursor:' + (isAvailable ? 'pointer' : 'default'),
          'transition:all 0.15s',
          'opacity:' + (isAvailable ? '1' : '0.45'),
          'position:relative',
        ].join(';');

        if (!isAvailable) {
          // Strikethrough line for unavailable
          vBtn.style.textDecoration = 'line-through';
        }

        vBtn.textContent = val;

        vBtn.addEventListener('click', function () {
          if (!isAvailable) return;
          selected[optIdx] = val;
          // Update all buttons in this row
          btnRow.querySelectorAll('button').forEach(b => {
            const isSelected = b.dataset.val === val;
            b.style.border = isSelected ? '2px solid #0D0D0D' : '2px solid rgba(0,0,0,0.15)';
            b.style.background = isSelected ? '#0D0D0D' : '#fff';
            b.style.color = isSelected ? '#fff' : (b.dataset.available === 'false' ? 'rgba(13,13,13,0.3)' : '#0D0D0D');
          });
          updateVariantImage();
          updatePrice();
          updateSubtotal();
          updateAtcState();
        });

        btnRow.appendChild(vBtn);
      });

      group.appendChild(btnRow);
      optionsWrap.appendChild(group);
    });
    if (!isDefaultVariantOnly) body.appendChild(optionsWrap);

    /* ── Customer message field (instant — from pre-rendered map) ── */
    var msgTextarea = null;
    var msgErrorEl = null;
    var msgMeta  = CZX_MSG_MAP[p.handle] || {};
    var allowMsg = liveMeta ? (liveMeta.allow_message === true) : (msgMeta.allow === true);
    var msgDesc  = liveMeta ? (liveMeta.message_desc || '') : (msgMeta.desc  || '');
    var msgLimit = liveMeta ? (liveMeta.message_limit || 0) : (msgMeta.limit || 0);

    if (allowMsg) {
      const msgWrap = document.createElement('div');
      msgWrap.style.cssText = 'display:flex;flex-direction:column;gap:6px;';
      if (msgDesc) {
        const descEl = document.createElement('p');
        descEl.style.cssText = 'font-size:0.875rem;color:rgba(13,13,13,0.7);margin:0;line-height:1.5;';
        descEl.textContent = msgDesc;
        msgWrap.appendChild(descEl);
      }
      msgTextarea = document.createElement('textarea');
      msgTextarea.placeholder = 'Enter your message here…';
      msgTextarea.rows = 3;
      if (msgLimit > 0) msgTextarea.maxLength = msgLimit;
      msgTextarea.style.cssText = [
        'width:100%;box-sizing:border-box',
        'border:1.5px solid rgba(0,0,0,0.15)',
        'border-radius:8px',
        'padding:10px 12px',
        'font-size:0.9rem;font-family:Boogaloo,cursive',
        'color:#0D0D0D;background:#fff',
        'resize:vertical;transition:border-color 0.15s;outline:none',
      ].join(';');
      msgTextarea.addEventListener('focus', () => { msgTextarea.style.borderColor = '#0D0D0D'; });
      msgTextarea.addEventListener('blur',  () => { if (msgTextarea.value.trim() === '') msgTextarea.style.borderColor = 'rgba(0,0,0,0.15)'; });
      msgTextarea.addEventListener('input', () => {
        if (msgTextarea.value.trim() !== '') {
          msgTextarea.style.borderColor = '#0D0D0D';
          msgTextarea.style.boxShadow = '';
          if (msgErrorEl) msgErrorEl.style.display = 'none';
          // Restore the ATC button
          atcBtn.disabled = false;
          updateAtcState();
        }
      });
      msgWrap.appendChild(msgTextarea);
      msgErrorEl = document.createElement('p');
      msgErrorEl.textContent = '⚠️ Please enter a message before adding to cart.';
      msgErrorEl.style.cssText = 'font-size:0.8rem;color:#E63946;margin:0;display:none;';
      msgWrap.appendChild(msgErrorEl);
      if (msgLimit > 0) {
        const ctr = document.createElement('p');
        ctr.style.cssText = 'font-size:0.75rem;color:rgba(13,13,13,0.4);text-align:right;margin:0;';
        ctr.textContent = '0 / ' + msgLimit;
        msgTextarea.addEventListener('input', () => { ctr.textContent = msgTextarea.value.length + ' / ' + msgLimit; });
        msgWrap.appendChild(ctr);
      }
      body.appendChild(msgWrap);
    }

    /* ── Photo upload field (from pre-rendered map) ── */
    var photoUrlInput = null;  /* holds uploaded CDN URL */
    var photoErrorEl  = null;
    var photoMeta  = CZX_PHOTO_MAP[p.handle] || {};
    var allowPhoto = liveMeta ? (liveMeta.allow_photo === true) : (photoMeta.allow === true);
    var photoDesc  = (liveMeta ? liveMeta.photo_desc : photoMeta.desc) || 'Upload Your Photo';

    if (allowPhoto) {
      const photoWrap = document.createElement('div');
      photoWrap.style.cssText = 'display:flex;flex-direction:column;gap:6px;';

      const photoLbl = document.createElement('p');
      photoLbl.style.cssText = 'font-size:0.9rem;font-weight:600;color:#0D0D0D;margin:0;';
      photoLbl.textContent = '📸 ' + photoDesc;
      photoWrap.appendChild(photoLbl);

      const photoHint = document.createElement('p');
      photoHint.style.cssText = 'font-size:0.78rem;color:rgba(13,13,13,0.55);margin:0;';
      photoHint.textContent = 'JPG, PNG or WebP · Max 20MB';
      photoWrap.appendChild(photoHint);

      /* Drop zone / file label */
      const photoZone = document.createElement('label');
      photoZone.style.cssText = [
        'display:flex;flex-direction:column;align-items:center;justify-content:center;gap:6px',
        'border:2px dashed rgba(0,0,0,0.2);border-radius:10px',
        'padding:18px 12px;cursor:pointer;text-align:center',
        'transition:border-color 0.15s,background 0.15s;background:rgba(0,0,0,0.02)',
      ].join(';');
      const photoInput = document.createElement('input');
      photoInput.type = 'file';
      photoInput.accept = 'image/jpeg,image/png,image/webp';
      photoInput.style.cssText = 'position:absolute;width:1px;height:1px;opacity:0;pointer-events:none;';
      const photoCta = document.createElement('span');
      photoCta.style.cssText = 'font-size:0.9rem;color:#0D0D0D;';
      photoCta.innerHTML = '⬆️ Choose a photo';
      photoZone.appendChild(photoInput);
      photoZone.appendChild(photoCta);
      photoWrap.appendChild(photoZone);

      /* Preview row */
      const photoPreview = document.createElement('div');
      photoPreview.style.cssText = 'display:none;align-items:center;gap:10px;margin-top:4px;';
      const photoThumb = document.createElement('img');
      photoThumb.style.cssText = 'width:48px;height:48px;object-fit:cover;border-radius:8px;border:1.5px solid rgba(0,0,0,0.1);';
      const photoName = document.createElement('span');
      photoName.style.cssText = 'font-size:0.82rem;color:#0D0D0D;flex:1;overflow:hidden;text-overflow:ellipsis;white-space:nowrap;';
      const photoRemove = document.createElement('button');
      photoRemove.type = 'button';
      photoRemove.textContent = '✕';
      photoRemove.style.cssText = 'border:none;background:rgba(0,0,0,0.06);color:#0D0D0D;border-radius:6px;width:28px;height:28px;cursor:pointer;font-size:0.9rem;';
      photoPreview.appendChild(photoThumb);
      photoPreview.appendChild(photoName);
      photoPreview.appendChild(photoRemove);
      photoWrap.appendChild(photoPreview);

      /* Hidden input that stores the uploaded URL */
      photoUrlInput = document.createElement('input');
      photoUrlInput.type = 'hidden';
      photoUrlInput.value = '';

      photoErrorEl = document.createElement('p');
      photoErrorEl.style.cssText = 'font-size:0.8rem;color:#E63946;margin:0;display:none;';
      photoWrap.appendChild(photoErrorEl);

      function showPhotoError(msg) {
        photoErrorEl.textContent = msg;
        photoErrorEl.style.display = 'block';
      }
      function clearPhotoError() { photoErrorEl.style.display = 'none'; }

      function uploadPhoto(file) {
        photoCta.textContent = 'Uploading…';
        var fd = new FormData();
        fd.append('file', file);
        fetch('/cart/uploads.json', {
          method: 'POST',
          headers: { 'X-Requested-With': 'XMLHttpRequest' },
          body: fd
        })
        .then(function(res) { return res.json(); })
        .then(function(data) {
          var url = data && data.file && data.file.url;
          if (url) {
            if (url.indexOf('//') === 0) url = 'https:' + url;
            photoUrlInput.value = url;
            photoName.textContent = file.name + ' ✓';
          } else {
            photoUrlInput.value = '[uploaded] ' + file.name;
            photoName.textContent = file.name;
          }
          atcBtn.disabled = false;
          updateAtcState();
        })
        .catch(function() {
          photoUrlInput.value = '[uploaded] ' + file.name;
          photoName.textContent = file.name;
        });
      }

      function processPhoto(file) {
        if (!file.type.match(/^image\/(jpeg|png|webp)$/)) {
          showPhotoError('⚠️ Please upload a JPG, PNG or WebP image.');
          return;
        }
        if (file.size > 20 * 1024 * 1024) {
          showPhotoError('⚠️ File is too large. Maximum size is 20MB.');
          return;
        }
        clearPhotoError();
        var reader = new FileReader();
        reader.onload = function(ev) {
          photoThumb.src = ev.target.result;
          photoZone.style.display = 'none';
          photoPreview.style.display = 'flex';
        };
        reader.readAsDataURL(file);
        uploadPhoto(file);
      }

      photoInput.addEventListener('change', function() {
        if (photoInput.files && photoInput.files[0]) processPhoto(photoInput.files[0]);
      });
      photoRemove.addEventListener('click', function() {
        photoInput.value = '';
        photoUrlInput.value = '';
        photoThumb.src = '';
        photoName.textContent = '';
        photoPreview.style.display = 'none';
        photoZone.style.display = 'flex';
        clearPhotoError();
        updateAtcState();
      });

      body.appendChild(photoWrap);
    }

    /* ── Quantity stepper ── */
    const qtySection = document.createElement('div');
    qtySection.style.cssText = 'display:flex;flex-direction:column;gap:8px;';
    const qtyLbl = document.createElement('p');
    qtyLbl.style.cssText = 'font-size:0.75rem;letter-spacing:0.08em;text-transform:uppercase;color:rgba(13,13,13,0.5);margin:0;';
    qtyLbl.textContent = 'Quantity';
    const qtyRow = document.createElement('div');
    qtyRow.style.cssText = 'display:flex;align-items:center;border:2px solid rgba(0,0,0,0.12);border-radius:10px;overflow:hidden;width:100%;';

    const mkQtyBtn = (label, action) => {
      const b = document.createElement('button');
      b.textContent = label;
      b.style.cssText = 'width:44px;height:44px;border:none;background:rgba(0,0,0,0.04);color:#0D0D0D;font-family:Boogaloo,cursive;font-size:1.3rem;cursor:pointer;display:flex;align-items:center;justify-content:center;transition:background 0.15s;';
      b.addEventListener('mouseover', () => { b.style.background = 'rgba(0,0,0,0.09)'; });
      b.addEventListener('mouseout', () => { b.style.background = 'rgba(0,0,0,0.04)'; });
      b.addEventListener('click', () => { action(); qtyDisplay.textContent = qty; updateSubtotal(); });
      return b;
    };
    const minusBtn = mkQtyBtn('−', () => { if (qty > 1) qty--; });
    const qtyDisplay = document.createElement('span');
    qtyDisplay.textContent = String(qty);
    qtyDisplay.style.cssText = 'flex:1;text-align:center;font-family:Boogaloo,cursive;font-size:1rem;font-weight:700;color:#0D0D0D;border-left:2px solid rgba(0,0,0,0.12);border-right:2px solid rgba(0,0,0,0.12);line-height:44px;user-select:none;';
    const plusBtn = mkQtyBtn('+', () => { if (qty < 99) qty++; });
    qtyRow.appendChild(minusBtn);
    qtyRow.appendChild(qtyDisplay);
    qtyRow.appendChild(plusBtn);
    qtySection.appendChild(qtyLbl);
    qtySection.appendChild(qtyRow);
    body.appendChild(qtySection);

    /* ── Subtotal ── matches product page style ── */
    const subtotalRow = document.createElement('div');
    subtotalRow.style.cssText = 'display:flex;align-items:center;justify-content:space-between;padding:12px 16px;background:rgba(255,221,0,0.12);border:2px solid rgba(255,221,0,0.5);border-radius:8px;';
    const subtotalLbl = document.createElement('span');
    subtotalLbl.style.cssText = 'font-family:Boogaloo,cursive;font-size:0.82rem;color:rgba(13,13,13,0.55);letter-spacing:0.08em;text-transform:uppercase;';
    subtotalLbl.textContent = 'Subtotal';
    const subtotalVal = document.createElement('span');
    subtotalVal.style.cssText = 'font-family:Boogaloo,cursive;font-size:1.15rem;font-weight:700;color:#0D0D0D;';
    function updateSubtotal() {
      const v = getSelectedVariant();
      const unitPrice = v ? v.price : (p.price ? parseInt(p.price, 10) : 0);
      subtotalVal.textContent = (unitPrice * qty / 100).toLocaleString('en-US', { style: 'currency', currency: 'USD' });
    }
    subtotalRow.appendChild(subtotalLbl);
    subtotalRow.appendChild(subtotalVal);
    body.appendChild(subtotalRow);

    /* ── Add to Cart button — CRAZXY style matching product page ── */
    const atcBtn = document.createElement('button');
    atcBtn.style.cssText = [
      'width:100%;padding:14px',
      'background:#FFDD00;color:#0D0D0D',
      'border:2.5px solid #0D0D0D;border-radius:4px',
      'box-shadow:3px 3px 0 #0D0D0D',
      'font-family:Boogaloo,cursive;font-size:1.1rem;letter-spacing:0.04em',
      'cursor:pointer',
      'transition:transform 0.15s,box-shadow 0.15s,background 0.15s',
      'display:flex;align-items:center;justify-content:center;gap:8px',
    ].join(';');
    atcBtn.innerHTML = '<svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round"><circle cx="9" cy="21" r="1"/><circle cx="20" cy="21" r="1"/><path d="M1 1h4l2.68 13.39a2 2 0 0 0 2 1.61h9.72a2 2 0 0 0 2-1.61L23 6H6"/></svg><span>Add to Cart</span>';
    atcBtn.addEventListener('mouseover', () => {
      if (!atcBtn.disabled) { atcBtn.style.transform='translate(-2px,-2px)'; atcBtn.style.boxShadow='5px 5px 0 #0D0D0D'; }
    });
    atcBtn.addEventListener('mouseout', () => {
      if (!atcBtn.disabled) { atcBtn.style.transform=''; atcBtn.style.boxShadow='3px 3px 0 #0D0D0D'; }
    });
    atcBtn.addEventListener('mousedown', () => {
      if (!atcBtn.disabled) { atcBtn.style.transform='translate(1px,1px)'; atcBtn.style.boxShadow='1px 1px 0 #0D0D0D'; }
    });

    function updateAtcState() {
      const v = getSelectedVariant();
      const allSelected = product.options.every((_, i) => selected[i] !== undefined);
      atcBtn.style.transform = '';
      if (!allSelected) {
        atcBtn.querySelector('span').textContent = 'Select options';
        atcBtn.disabled = false;
        atcBtn.style.background = 'rgba(13,13,13,0.15)';
        atcBtn.style.boxShadow = '3px 3px 0 rgba(0,0,0,0.2)';
        atcBtn.style.color = 'rgba(13,13,13,0.5)';
      } else if (!v || !v.available) {
        atcBtn.querySelector('span').textContent = 'Unavailable';
        atcBtn.disabled = true;
        atcBtn.style.background = 'rgba(13,13,13,0.1)';
        atcBtn.style.boxShadow = 'none';
        atcBtn.style.color = 'rgba(13,13,13,0.35)';
      } else {
        atcBtn.querySelector('span').textContent = 'Add to Cart';
        atcBtn.disabled = false;
        atcBtn.style.background = '#FFDD00';
        atcBtn.style.boxShadow = '3px 3px 0 #0D0D0D';
        atcBtn.style.color = '#0D0D0D';
      }
    }

    atcBtn.addEventListener('click', async function () {
      const v = getSelectedVariant();
      const allSelected = product.options.every((_, i) => selected[i] !== undefined);
      if (!allSelected) {
        atcBtn.querySelector('span').textContent = 'Select options first';
        atcBtn.style.background = '#E63946';
        atcBtn.style.color = '#fff';
        setTimeout(() => updateAtcState(), 1500);
        return;
      }
      if (!v || !v.available) return;

      // Validate message field before showing loading state
      if (msgTextarea !== null && msgTextarea.value.trim() === '') {
        msgTextarea.style.borderColor = '#E63946';
        msgTextarea.style.boxShadow = '0 0 0 2px rgba(230,57,70,0.15)';
        if (msgErrorEl) msgErrorEl.style.display = 'block';
        const origBtnHTML = '<svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round"><circle cx="9" cy="21" r="1"/><circle cx="20" cy="21" r="1"/><path d="M1 1h4l2.68 13.39a2 2 0 0 0 2 1.61h9.72a2 2 0 0 0 2-1.61L23 6H6"/></svg><span>Add to Cart</span>';
        atcBtn.innerHTML = '<svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round"><line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/></svg>';
        atcBtn.style.background = '#E63946';
        atcBtn.style.color = '#fff';
        atcBtn.style.boxShadow = '3px 3px 0 #0D0D0D';
        atcBtn.disabled = true;
        setTimeout(() => {
          atcBtn.innerHTML = origBtnHTML;
          atcBtn.disabled = false;
          updateAtcState();
        }, 2000);
        msgTextarea.focus();
        return;
      }

      // Validate photo upload — required if the field is shown
      if (photoUrlInput !== null && !photoUrlInput.value) {
        if (photoErrorEl) {
          photoErrorEl.textContent = '⚠️ Please upload a photo before adding to cart.';
          photoErrorEl.style.display = 'block';
        }
        const origBtnHTML2 = '<svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round"><circle cx="9" cy="21" r="1"/><circle cx="20" cy="21" r="1"/><path d="M1 1h4l2.68 13.39a2 2 0 0 0 2 1.61h9.72a2 2 0 0 0 2-1.61L23 6H6"/></svg><span>Add to Cart</span>';
        atcBtn.innerHTML = '<svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round"><line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/></svg>';
        atcBtn.style.background = '#E63946';
        atcBtn.style.color = '#fff';
        atcBtn.style.boxShadow = '3px 3px 0 #0D0D0D';
        atcBtn.disabled = true;
        setTimeout(() => {
          atcBtn.innerHTML = origBtnHTML2;
          atcBtn.disabled = false;
          updateAtcState();
        }, 2000);
        return;
      }

      atcBtn.disabled = true;
      atcBtn.style.transform = '';
      atcBtn.style.boxShadow = '1px 1px 0 #0D0D0D';
      atcBtn.style.background = 'rgba(255,221,0,0.5)';
      atcBtn.querySelector('span').textContent = 'Adding…';

      const cartProps = {};
      if (msgTextarea && msgTextarea.value.trim()) cartProps['Message'] = msgTextarea.value.trim();
      if (photoUrlInput && photoUrlInput.value) cartProps['Photo'] = photoUrlInput.value;

      try {
        const res = await fetch('/cart/add.js', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ id: v.id, quantity: qty, properties: cartProps }),
        });
        if (!res.ok) throw new Error();
        atcBtn.style.background = '#06D6A0';
        atcBtn.style.boxShadow = '3px 3px 0 #0D0D0D';
        atcBtn.style.color = '#fff';
        atcBtn.querySelector('span').textContent = 'Added ✓';
        document.dispatchEvent(new CustomEvent('cart:update', { bubbles: true }));
        const drawer = document.querySelector('cart-drawer-component');
        if (drawer && drawer.open) drawer.open();
        setTimeout(() => modal.remove(), 1200);
      } catch {
        atcBtn.style.background = '#E63946';
        atcBtn.style.color = '#fff';
        atcBtn.querySelector('span').textContent = 'Error — retry';
        atcBtn.disabled = false;
        setTimeout(() => updateAtcState(), 2000);
      }
    });

    body.appendChild(atcBtn);
    sheet.appendChild(body);
    modal.appendChild(sheet);

    // Close on backdrop click
    modal.addEventListener('click', e => { if (e.target === modal) modal.remove(); });

    // Add keyframe if not already present
    if (!document.getElementById('czx-wl-modal-style')) {
      const style = document.createElement('style');
      style.id = 'czx-wl-modal-style';
      style.textContent = '@keyframes czxSheetUp{from{transform:translateY(100%)}to{transform:translateY(0)}}';
      document.head.appendChild(style);
    }

    document.body.appendChild(modal);
    updatePrice();
    updateSubtotal();
    updateAtcState();
  }
})();
